import type { TFunction } from "i18next";

export const EMAIL_BUILDER_MAX_TEST_RECIPIENTS = 5;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type TEmailBuilderTestRecipientsResult =
  | { ok: true; recipients: string[] }
  | { ok: false; error: string };

export function parseEmailBuilderTestRecipientInput(input: string): string[] {
  const tokens = input
    .split(/[\s,;]+/)
    .map(token => token.trim())
    .filter(token => token.length > 0);

  const seen = new Set<string>();
  const recipients: string[] = [];

  for (const token of tokens) {
    const normalized = token.toLowerCase();
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    recipients.push(token);
  }

  return recipients;
}

export function validateEmailBuilderTestRecipients(
  recipients: string[],
  t: TFunction,
): TEmailBuilderTestRecipientsResult {
  if (recipients.length === 0) {
    return {
      ok: false,
      error: t("Enter at least one email address."),
    };
  }

  if (recipients.length > EMAIL_BUILDER_MAX_TEST_RECIPIENTS) {
    return {
      ok: false,
      error: t("Enter at most {{max}} email addresses.", {
        max: EMAIL_BUILDER_MAX_TEST_RECIPIENTS,
      }),
    };
  }

  for (const recipient of recipients) {
    if (!EMAIL_PATTERN.test(recipient)) {
      return {
        ok: false,
        error: t('"{{recipient}}" is not a valid email address.', { recipient }),
      };
    }
  }

  return {
    ok: true,
    recipients,
  };
}

export function parseAndValidateEmailBuilderTestRecipients(
  input: string,
  t: TFunction,
): TEmailBuilderTestRecipientsResult {
  return validateEmailBuilderTestRecipients(parseEmailBuilderTestRecipientInput(input), t);
}
